/**
 * The tunables a **bombing raid** runs on, split out of `tunables.ts` for the
 * same reason `ai-tunables.ts` and `gunnery-tunables.ts` were. They merge
 * straight back into `DEFAULT_TUNABLES`, so downstream still sees one flat
 * `Tunables` record.
 *
 * One key here carries real provenance. The Mission Builder manual states the
 * bomb-run altitude outright — *"To be sure of getting a hit, bombers will make
 * their attack run at 1200 feet. When setting up an intercept mission, don't
 * put the intercepting scouts too high."* (docs/game/manual/05-mission-builder.md)
 * — which is a statement about the original's own bombing AI, so
 * `bombRunAltitudeM` is REPORTED rather than pending, and 1,200 ft is carried
 * as the metres it converts to.
 *
 * Everything else is CALIBRATION-PENDING in flight-tunables.md §8's sense, and
 * for a stronger reason than usual: **the game data has no bomb fields at all.**
 * `APLANE.DAT`'s 266-byte record carries no bomb load, no bomb damage and no
 * release logic (docs/formats/missions-campaign.md), and no decoded file marks
 * any ground object as destructible (docs/formats/gi-format.md finds `.GI` has
 * no consumer beyond ground collision). The bomb loads the generators hand out
 * come from the *manual's* aircraft tables — Gotha G.IV "up to 1,100 lb",
 * Handley Page "2,000 lb payload", D.H.4 "460 lb" — which are real-world
 * figures the manual reprints, not extracted values.
 */

import { METRES_PER_FOOT } from "./units.ts";

export const BOMB_TUNABLES = {
  /** Altitude a bombing run is flown at. REPORTED: the Mission Builder manual's own 1,200 ft. */
  bombRunAltitudeM: 1200 * METRES_PER_FOOT,
  /**
   * How close the *predicted impact point* has to be to the aiming point before
   * the first bomb of a stick goes. The prediction is real ballistics
   * (`bombs.ts`), so this is aiming accuracy, not a release-range fudge.
   *
   * It is deliberately smaller than `bombBlastRadiusM`: a tolerance wider than
   * the blast means a bomber that "had a solution" routinely drops outside its
   * own burst, which reads as a bomb model that does not work rather than as a
   * crew that missed. CALIBRATION-PENDING.
   */
  bombAimToleranceM: 45,
  /**
   * Seconds between the bombs of one stick. At a heavy's ~30 m/s that spaces
   * them about 11 m apart on the ground, and it is what decides how much of a
   * rack goes down in the two or three seconds a solution holds. CALIBRATION-PENDING.
   */
  bombReleaseIntervalSeconds: 0.35,
  /** Radius over which a burst still does something. CALIBRATION-PENDING. */
  bombBlastRadiusM: 60,
  /**
   * A bomb's full share, before the profile's own toughness scales it. It is 1
   * on purpose: that makes `bombVulnerability` mean exactly *"one over the
   * number of direct hits it takes to level this"*, so the per-kind figure in
   * `target-profiles.ts` is the dial a reader can reason about and this key is
   * only the global lever on all of them. CALIBRATION-PENDING like the rest.
   */
  bombDamageAtCentre: 1,
  /**
   * Downward speed a bomb already has at release, m/s. A bomb leaves the rack
   * with the aeroplane's velocity and nothing else, so this is zero — it exists
   * as a tunable only so a future rack-ejection model has somewhere to live.
   * CALIBRATION-PENDING.
   */
  bombEjectionSpeedMps: 0,
} satisfies Record<string, number>;

export type BombTunableKey = keyof typeof BOMB_TUNABLES;

/** The bomb-run altitude is the manual's; the rest go on the calibration queue. */
const SOURCED_BOMB_TUNABLES: readonly BombTunableKey[] = ["bombRunAltitudeM"];

export const BOMB_TUNABLE_KEYS: readonly BombTunableKey[] = (
  Object.keys(BOMB_TUNABLES) as BombTunableKey[]
).filter((key) => !SOURCED_BOMB_TUNABLES.includes(key));
